import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Plus, Clock, Calendar, DollarSign, Users, ChevronRight } from 'lucide-react';

import api from '../../../services/api';
import { setVariations } from '../../../services/leavePolicyService';
import Button from '../../../components/ui/Button';
import CheckboxInput from '../../../components/ui/CheckboxInput';

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: rgba(2, 145, 239, 0.06);
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #004242;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover { border-color: #0291EF; box-shadow: 0 1px 4px rgba(2, 145, 239, 0.1); }
`;

const ChipRemove = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  padding: 0;
  margin-left: 2px;
  &:hover { color: #E53935; }
`;

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 24px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const ModalTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 20px;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const DetailDesc = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const DetailGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #F5F7FA;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #64748b;
`;

const DetailLabel = styled.span`
  flex: 1;
`;

const DetailValue = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export default function LeavePolicyVariationsTab({ policyId }) {
  const { t } = useTranslation();
  const [assignedVariations, setAssignedVariations] = useState([]);
  const [allVariations, setAllVariations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detailVariation, setDetailVariation] = useState(null);

  const fetchPolicy = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/leave-policies/${policyId}`);
      setAssignedVariations(res.data?.data?.variations || []);
    } catch { /* interceptor */ }
    setLoading(false);
  }, [policyId]);

  useEffect(() => { fetchPolicy(); }, [fetchPolicy]);

  const handleOpenModal = useCallback(async () => {
    try {
      const res = await api.get('/v1/leave-variations');
      const result = res.data?.data;
      setAllVariations(Array.isArray(result) ? result : (result?.data || []));
    } catch { /* interceptor */ }
    setSelectedIds(assignedVariations.map((v) => v.id));
    setModalOpen(true);
  }, [assignedVariations]);

  const handleToggle = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleChipClick = useCallback(async (variationId) => {
    try {
      const res = await api.get(`/v1/leave-variations/${variationId}`);
      setDetailVariation(res.data?.data || null);
    } catch { /* interceptor */ }
  }, []);

  const handleRemove = useCallback(async (variationId) => {
    const remaining = assignedVariations.filter((v) => v.id !== variationId).map((v) => v.id);
    try {
      await setVariations(policyId, { leaveVariationIds: remaining });
      await fetchPolicy();
    } catch { /* interceptor */ }
  }, [policyId, assignedVariations, fetchPolicy]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await setVariations(policyId, { leaveVariationIds: selectedIds });
      await fetchPolicy();
      setModalOpen(false);
    } catch { /* interceptor */ }
    setSaving(false);
  }, [policyId, selectedIds, fetchPolicy]);

  if (loading) return <EmptyText>{t('common.loading')}</EmptyText>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button onClick={handleOpenModal}><Plus size={16} /> {t('leavePolicy.addVariation')}</Button>
      </div>

      {assignedVariations.length === 0 ? (
        <EmptyText>{t('leavePolicy.noVariations')}</EmptyText>
      ) : (
        <ChipList>
          {assignedVariations.map((v) => (
            <Chip key={v.id} onClick={() => handleChipClick(v.id)}>
              <span>{v.name}</span>
              <ChevronRight size={14} color="#A0A8B4" />
              <ChipRemove type="button" onClick={(e) => { e.stopPropagation(); handleRemove(v.id); }}><X size={14} /></ChipRemove>
            </Chip>
          ))}
        </ChipList>
      )}

      {modalOpen && (
        <Overlay onClick={() => setModalOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t('leavePolicy.selectVariations')}</ModalTitle>
            <OptionList>
              {allVariations.map((v) => (
                <CheckboxInput
                  key={v.id}
                  label={v.name}
                  checked={selectedIds.includes(v.id)}
                  onChange={() => handleToggle(v.id)}
                />
              ))}
            </OptionList>
            <DialogActions>
              <Button variant="outline" onClick={() => setModalOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? '...' : t('common.save')}
              </Button>
            </DialogActions>
          </ModalBox>
        </Overlay>
      )}

      {detailVariation && (
        <Overlay onClick={() => setDetailVariation(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <ModalTitle>{detailVariation.name}</ModalTitle>
            {detailVariation.description && (
              <DetailDesc>{detailVariation.description}</DetailDesc>
            )}
            <DetailGrid>
              <DetailItem>
                <Users size={14} />
                <DetailLabel>{t('leaveVariation.forGender')}</DetailLabel>
                <DetailValue>{detailVariation.forGender === 'all' ? t('leaveVariation.genderAll') : detailVariation.forGender === 'male' ? t('leaveVariation.genderMale') : t('leaveVariation.genderFemale')}</DetailValue>
              </DetailItem>
              {detailVariation.instanceLimit != null && (
                <DetailItem>
                  <Calendar size={14} />
                  <DetailLabel>{t('leaveVariation.instanceLimit')}</DetailLabel>
                  <DetailValue>{detailVariation.instanceLimit}</DetailValue>
                </DetailItem>
              )}
              {detailVariation.monthlyLimit != null && (
                <DetailItem>
                  <Calendar size={14} />
                  <DetailLabel>{t('leaveVariation.monthlyLimit')}</DetailLabel>
                  <DetailValue>{detailVariation.monthlyLimit}</DetailValue>
                </DetailItem>
              )}
              {detailVariation.cooldownDays != null && (
                <DetailItem>
                  <Clock size={14} />
                  <DetailLabel>{t('leaveVariation.cooldownDays')}</DetailLabel>
                  <DetailValue>{detailVariation.cooldownDays}</DetailValue>
                </DetailItem>
              )}
              <DetailItem>
                <DollarSign size={14} />
                <DetailLabel>{t('leaveVariation.isPaid')}</DetailLabel>
                <DetailValue>{detailVariation.isPaid ? t('common.yes') : t('common.no')}</DetailValue>
              </DetailItem>
              <DetailItem>
                <Clock size={14} />
                <DetailLabel>{t('leaveVariation.isHourly')}</DetailLabel>
                <DetailValue>{detailVariation.isHourly ? t('common.yes') : t('common.no')}</DetailValue>
              </DetailItem>
            </DetailGrid>
            <DialogActions>
              <Button variant="outline" onClick={() => setDetailVariation(null)}>{t('common.cancel')}</Button>
            </DialogActions>
          </ModalBox>
        </Overlay>
      )}
    </>
  );
}
